import { useMemo, useState, useEffect, useRef } from 'react';
import {
  ResponsiveContainer, LineChart, Line,
  XAxis, YAxis, CartesianGrid, ReferenceArea, ReferenceDot, ReferenceLine,
} from 'recharts';
import { LAP_COLORS, getLapColor } from '../LapSelector/LapSelector';
import { subscribeExtent } from '../../utils/chartExtentBus';
import { subscribeZoom, publishZoom } from '../../utils/chartZoomBus';
import { useTouchChartGestures } from '../../hooks/useTouchChartGestures';
import { usePanOnWheel } from '../../hooks/usePanOnWheel';
import PanelHeader from '../WindowFrame/headerSlot';
import HelpButton from '../HelpButton/HelpButton';
import { useCornerHighlight } from '../../hooks/useCornerHighlight';
import { convertDistanceFt, getDistanceUnit, TOOLTIP_FONT_SCALES } from '../../utils/unitConvert';
import styles from './GTrace.module.css';
import { useTheme } from '../../contexts/ThemeContext';
import { lastCursor } from '../../utils/chartCursor';
import { arrayMin, arrayMax } from '../../utils/extent';
import MultiChannelTrace, { ChannelPicker } from './MultiChannelTrace';
import { exportTracePanelPng } from '../../utils/traceExport';
import { niceTicks } from '../../utils/channelScale';

// Chart margin constants — must match the <LineChart> margin prop below
const CHART_TOP    = 8;
const CHART_BOTTOM = 22;  // margin.bottom (4) + XAxis ticks/label
const Y_AXIS_W     = 44;
const RIGHT_MARGIN = 12;
// Pointer travel (px) before a mousedown is treated as a zoom drag rather than
// a click. Below it the chart must not re-render, or React swaps the node the
// mousedown landed on and the browser never fires the click.
const DRAG_ARM_PX  = 4;

const M_TO_FT = 3.28084;

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

function fmtAxis(v) {
  if (v == null || !Number.isFinite(v)) return '';
  return Math.abs(v) >= 10 ? Math.round(v).toString() : v.toFixed(1);
}

function downsample(arr, maxPts) {
  if (arr.length <= maxPts) return arr;
  const step = arr.length / maxPts;
  return Array.from({ length: maxPts }, (_, i) => arr[Math.round(i * step)]);
}

function findNearest(points, distFt) {
  if (!points?.length) return null;
  let best = points[0];
  let bestDiff = Math.abs(points[0].dist_ft - distFt);
  for (let i = 1; i < points.length; i++) {
    const diff = Math.abs(points[i].dist_ft - distFt);
    if (diff < bestDiff) { best = points[i]; bestDiff = diff; }
  }
  return best;
}

function fmtVal(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1000) return Math.round(v).toLocaleString();
  if (abs >= 100)  return Math.round(v).toString();
  if (abs >= 10)   return v.toFixed(1);
  return v.toFixed(2);
}

function buildSeries(lapTraces, selectedLaps, palette, keyPrefix, dataKey, zoomDomain, valueTransform, customColors = null, driverSource = 'primary') {
  return selectedLaps.map((lapIndex, i) => {
    const colorIdx = i;
    const trace = lapTraces[lapIndex];
    if (!trace) return null;

    const raw = trace.points.filter(p =>
      p.dist_m != null && p.lat && p.lon && p[dataKey] != null
    );
    if (!raw.length) return null;

    const source = zoomDomain
      ? raw.filter(p => {
          const ft = p.dist_m * M_TO_FT;
          return ft >= zoomDomain[0] && ft <= zoomDomain[1];
        })
      : raw;

    const sampled = downsample(source.length > 0 ? source : raw, 5000);
    const lapStartTime = trace.start_time ?? 0;
    const points = sampled.map(p => {
      const val = p[dataKey];
      return {
        dist_ft:  Math.round(p.dist_m * M_TO_FT),
        value:    (valueTransform && val != null) ? valueTransform(val) : val,
        lat:      p.lat,
        lon:      p.lon,
        csv_time: lapStartTime + (p.time ?? 0),
      };
    });

    const maxPt = points.reduce((b, p) => (p.value != null && (b == null || p.value > b.value)) ? p : b, null);
    const minPt = points.reduce((b, p) => (p.value != null && (b == null || p.value < b.value)) ? p : b, null);

    return {
      lapIndex,
      lapNumber: trace.lap_number ?? lapIndex,
      source:    driverSource,
      seriesKey: `${keyPrefix}${lapIndex}`,
      colorIdx,
      lapLabel: trace.lap_time_str || `L${trace.lap_number}`,
      color:    (customColors && customColors[lapIndex]) || palette[colorIdx % palette.length],
      points, maxPt, minPt,
    };
  }).filter(Boolean);
}

// ── Following dots ────────────────────────────────────────────────────────────
// `valueToFrac` maps a real channel value → its 0..1 height fraction (0 = bottom,
// 1 = top); this lets it follow the asymmetric zero-split scale when active.
function HoverDots({ data, valueToFrac, wrapHeight }) {
  if (!data?.entries?.length || wrapHeight == null) return null;
  const chartH = wrapHeight - CHART_TOP - CHART_BOTTOM;
  if (chartH <= 0) return null;

  return (
    <>
      {data.entries.map(e => {
        if (e.value == null) return null;
        const frac = valueToFrac(e.value);
        if (frac == null || !Number.isFinite(frac)) return null;
        const yPx  = CHART_TOP + (1 - frac) * chartH;
        return (
          <div
            key={e.seriesKey}
            style={{
              position:      'absolute',
              left:          data.x - 5,
              top:           yPx - 5,
              width:         10,
              height:        10,
              borderRadius:  '50%',
              background:    e.color,
              border:        '2px solid #fff',
              pointerEvents: 'none',
              zIndex:        15,
              boxShadow:     `0 0 4px ${e.color}88`,
            }}
          />
        );
      })}
    </>
  );
}

// ── Imperative hover tooltip ──────────────────────────────────────────────────
function HoverTooltip({ data, yLabel, wrapWidth, anchoredTooltip, unitPrefs = {} }) {
  if (!data?.entries?.length) return null;
  const { x, distFt, entries, side } = data;
  const scale = TOOLTIP_FONT_SCALES[unitPrefs.tooltipFontScale] ?? 1;

  const TOOLTIP_W = Math.round(250 * scale);
  let leftVal, rightVal;
  if (anchoredTooltip) {
    leftVal  = side === 'left'  ? 50 : undefined;
    rightVal = side === 'right' ? 10 : undefined;
  } else {
    const flipLeft = x > (wrapWidth ?? 600) * 0.62;
    leftVal  = flipLeft ? undefined : x + 14;
    rightVal = flipLeft ? (wrapWidth - x + 8) : undefined;
  }

  return (
    <div
      className={styles.tooltip}
      style={{
        position:      'absolute',
        top:           10,
        left:          leftVal,
        right:         rightVal,
        pointerEvents: 'none',
        zIndex:        20,
        maxWidth:      TOOLTIP_W,
        fontSize:      `${(0.78 * scale).toFixed(3)}rem`,
      }}
    >
      <div className={styles.tooltipDist}>
        {(convertDistanceFt(distFt, unitPrefs) / 1000).toFixed(2)}k {getDistanceUnit(unitPrefs)}
      </div>

      {entries.map((e, i) => {
        const refVal = entries[0].value;
        const delta  = i > 0 && refVal != null && e.value != null
          ? e.value - refVal : null;

        return (
          <div key={e.seriesKey} className={styles.tooltipRow} style={{ borderLeftColor: e.color }}>
            <span className={styles.tooltipLabel}>{e.lapLabel}</span>
            {e.value != null && (
              <span className={styles.tooltipVal}>
                {fmtVal(e.value)}
                {yLabel && <span className={styles.tooltipUnit}> {yLabel}</span>}
              </span>
            )}
            {delta != null && (
              <span
                style={{
                  fontSize: '0.897em',
                  fontVariantNumeric: 'tabular-nums',
                  color: delta > 0 ? '#44cc88' : delta < 0 ? '#e8411a' : '#555',
                  whiteSpace: 'nowrap',
                }}
              >
                {delta > 0 ? '+' : ''}{fmtVal(delta)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function SingleChannelTrace({
  lapTraces, selectedLaps, height,
  compareDrivers = [],
  customColors1 = null,
  dataKey, title, yLabel, yDomain, yTickFormatter,
  valueTransform,
  anchoredTooltip = false,
  unitPrefs = {},
  zeroLine = false,
  zeroFracKey = 'channel.zeroFrac',
  headerExtra = null,
  helpPanel = 'channeltrace',
  // Optional panel-local Channels picker. A caller that wants extra channels
  // rendered switches to MultiChannelTrace once one is picked (see PowerTrace);
  // exposing the picker here lets the single-signal chart keep its zero-split
  // scale until then.
  availableChannels = [],
  selectedAdditionalChannels = [],
  onAdditionalChannelToggle = null,
  // PNG export metadata
  signalName = title,
  exportSlug = 'channeltrace',
  trackName = '',
  sessionDate = '',
  sessionVehicle = '',
  driverName1 = 'Driver 1',
  exportCompareDrivers = [],
}) {
  const { chartColors } = useTheme();
  const cornerHl = useCornerHighlight();
  const [showMinMax,     setShowMinMax]     = useState(true);
  const [zoomDomain,     setZoomDomain]     = useState(null);
  const [naturalExtent,  setNaturalExtent]  = useState(null);
  const [isSelecting,    setIsSelecting]    = useState(false);
  const [selectionViz,   setSelectionViz]   = useState(null);
  const [tooltipData,    setTooltipData]    = useState(null);
  const [wrapHeight,     setWrapHeight]     = useState(null);
  const [isAnnotateMode, setIsAnnotateMode] = useState(false);
  const [zeroFracRaw,    setZeroFracRaw]    = useState(() => {
    try { const s = localStorage.getItem(zeroFracKey); return s != null ? parseFloat(s) : null; }
    catch { return null; }
  });

  const chartWrapRef    = useRef(null);
  const hoverLineRef    = useRef(null);
  const seriesRef       = useRef([]);
  const zoomDomainRef   = useRef(null);
  const naturalExtentRef = useRef(null);
  const isSelectingRef    = useRef(false);
  const isAnnotateModeRef = useRef(false);
  const isDirectHoverRef  = useRef(false);
  const tooltipSideRef    = useRef('right');
  const refAreaLeft       = useRef(null);
  const refAreaRight      = useRef(null);
  const pendingDragRef    = useRef(null);
  const finalizeZoomRef   = useRef(null);
  const draggingZeroRef   = useRef(false);
  const zeroFracLiveRef   = useRef(null);

  // Convert chart pixel x → dist_ft (used by touch gesture hook)
  const pixelToDistFt = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return null;
    const zoom = zoomDomainRef.current;
    const nat  = naturalExtentRef.current;
    const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
    if (!allDists.length) return null;
    const xMin = zoom ? zoom[0] : (nat?.[0] ?? arrayMin(allDists));
    const xMax = zoom ? zoom[1] : (nat?.[1] ?? arrayMax(allDists));
    if (xMax === xMin) return null;
    const rect   = wrap.getBoundingClientRect();
    const chartW = rect.width - Y_AXIS_W - RIGHT_MARGIN;
    if (chartW <= 0) return null;
    const t = Math.max(0, Math.min(1, (clientX - rect.left - Y_AXIS_W) / chartW));
    return xMin + t * (xMax - xMin);
  };
  const pixelToDistFtRef = useRef(pixelToDistFt);
  pixelToDistFtRef.current = pixelToDistFt;

  // Crosshair move/clear callbacks for the touch gesture hook
  const onCrosshairMove = (clientX) => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const distFt = pixelToDistFtRef.current(clientX);
    if (distFt == null) return;
    const rect   = wrap.getBoundingClientRect();
    const chartW = wrap.clientWidth - Y_AXIS_W - RIGHT_MARGIN;
    const xPx    = Y_AXIS_W + Math.max(0, Math.min(chartW, clientX - rect.left - Y_AXIS_W));
    const line = hoverLineRef.current;
    if (line) { line.style.left = `${xPx}px`; line.style.display = 'block'; }
    const td = buildTooltipFromDist(distFt);
    if (td) setTooltipData(td);
    const nearest = findNearest(seriesRef.current[0]?.points ?? [], distFt);
    if (nearest?.lat && nearest?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
        detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime: nearest.csv_time ?? null },
      }));
    }
  };
  const onCrosshairMoveRef = useRef(onCrosshairMove);
  onCrosshairMoveRef.current = onCrosshairMove;

  const onCrosshairClear = () => {
    isDirectHoverRef.current = false;
    setTooltipData(null);
    if (hoverLineRef.current) hoverLineRef.current.style.display = 'none';
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };
  const onCrosshairClearRef = useRef(onCrosshairClear);
  onCrosshairClearRef.current = onCrosshairClear;

  const series1 = useMemo(
    () => buildSeries(lapTraces, selectedLaps, LAP_COLORS, 's1-', dataKey, zoomDomain, valueTransform, customColors1),
    [lapTraces, selectedLaps, dataKey, zoomDomain, valueTransform, customColors1]
  );
  const series2 = useMemo(
    () => compareDrivers.flatMap(d =>
      buildSeries(d.lapTraces, d.selectedLaps, d.palette, `s2-${d.slot}-`, dataKey, zoomDomain, valueTransform, d.customColors, d.slot)),
    [compareDrivers, dataKey, zoomDomain, valueTransform]
  );
  const series = useMemo(() => [...series1, ...series2], [series1, series2]);

  useEffect(() => { seriesRef.current = series; }, [series]);

  // Subscribe to SpeedTrace's x-extent so x-axis scales match
  useEffect(() => {
    return subscribeExtent(ext => {
      naturalExtentRef.current = ext;
      setNaturalExtent(ext);
    });
  }, []);

  // Track chart wrapper height for dot y-positioning
  useEffect(() => {
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => setWrapHeight(wrap.clientHeight));
    ro.observe(wrap);
    setWrapHeight(wrap.clientHeight);
    return () => ro.disconnect();
  }, []);

  // Sync zoom with the shared bus — receives current value immediately on mount.
  useEffect(() => {
    return subscribeZoom(domain => {
      setZoomDomain(domain);
      zoomDomainRef.current = domain;
    });
  }, []);

  // Listen for CommentsPanel toggling annotate mode
  useEffect(() => {
    const handler = (e) => {
      const active = !!e.detail?.active;
      setIsAnnotateMode(active);
      isAnnotateModeRef.current = active;
    };
    window.addEventListener('trackapp:annotate-mode', handler);
    return () => window.removeEventListener('trackapp:annotate-mode', handler);
  }, []);

  // Global mouseup — finalizes zoom even when mouse leaves chart
  useEffect(() => {
    const handler = () => finalizeZoomRef.current?.();
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, []);

  // Helper: compute x-pixel and tooltip entries from a distFt value
  function buildTooltipFromDist(distFt) {
    const wrap = chartWrapRef.current;
    if (!wrap) return null;
    const zoom = zoomDomainRef.current;
    const nat  = naturalExtentRef.current;
    const allDists = seriesRef.current.flatMap(s => s.points.map(p => p.dist_ft));
    if (!allDists.length) return null;
    const xMin = zoom ? zoom[0] : (nat?.[0] ?? arrayMin(allDists));
    const xMax = zoom ? zoom[1] : (nat?.[1] ?? arrayMax(allDists));
    if (xMax === xMin) return null;

    const chartW = wrap.clientWidth - Y_AXIS_W - RIGHT_MARGIN;
    const t   = Math.max(0, Math.min(1, (distFt - xMin) / (xMax - xMin)));
    const xPx = Y_AXIS_W + t * chartW;

    const entries = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      if (!pt) return null;
      return { seriesKey: s.seriesKey, lapLabel: s.lapLabel, color: s.color, value: pt.value };
    }).filter(Boolean);

    if (!entries.length) return null;
    const ww = wrap.clientWidth ?? 600;
    const frac = xPx / ww;
    if (frac < 0.45) tooltipSideRef.current = 'right';
    else if (frac > 0.55) tooltipSideRef.current = 'left';
    return { x: xPx, distFt, entries, side: tooltipSideRef.current };
  }

  // Map-hover event → move hover line + show tooltip
  useEffect(() => {
    const handler = (e) => {
      const line = hoverLineRef.current;
      const distFt = e.detail?.distFt;

      if (distFt == null) {
        if (line) line.style.display = 'none';
        if (!isDirectHoverRef.current) setTooltipData(null);
        return;
      }

      const zoom = zoomDomainRef.current;
      if (zoom && (distFt < zoom[0] || distFt > zoom[1])) {
        if (line) line.style.display = 'none';
        if (!isDirectHoverRef.current) setTooltipData(null);
        return;
      }

      const td = buildTooltipFromDist(distFt);
      if (line && td) { line.style.left = `${td.x}px`; line.style.display = 'block'; }
      else if (line)  { line.style.display = 'none'; }

      if (!isDirectHoverRef.current) setTooltipData(td);
    };

    window.addEventListener('trackapp:map-hover', handler);
    return () => window.removeEventListener('trackapp:map-hover', handler);
  }, []);

  // Chart-hover event (another trace is hovered) → move hover line + show tooltip
  useEffect(() => {
    const handler = (e) => {
      if (isDirectHoverRef.current) return;

      const distFt = e.detail?.distFt;
      const line   = hoverLineRef.current;

      if (distFt == null) {
        setTooltipData(null);
        if (line) line.style.display = 'none';
        return;
      }

      const zoom = zoomDomainRef.current;
      if (zoom && (distFt < zoom[0] || distFt > zoom[1])) {
        setTooltipData(null);
        if (line) line.style.display = 'none';
        return;
      }

      const td = buildTooltipFromDist(distFt);
      if (line && td) { line.style.left = `${td.x}px`; line.style.display = 'block'; }
      else if (line)  { line.style.display = 'none'; }
      setTooltipData(td);
    };

    window.addEventListener('trackapp:chart-hover', handler);
    // Late mount (mobile tab switch): show the last cursor position.
    const last = lastCursor();
    if (last) handler({ detail: last });
    return () => window.removeEventListener('trackapp:chart-hover', handler);
  }, []);

  // ── Zoom drag ─────────────────────────────────────────────────────────────
  // Record the press but touch no state yet — see emitCursorClick above.
  const handleMouseDown = (e, event) => {
    const clientX = event?.clientX;
    const distFt = clientX != null
      ? pixelToDistFtRef.current(clientX)
      : (e?.activeLabel != null ? Number(e.activeLabel) : null);
    if (distFt == null) return;
    pendingDragRef.current = { distFt, clientX: clientX ?? null };
  };

  // A press that never armed a drag is a plain click. Recharts' own onClick
  // cannot carry this: the chart re-renders between mousedown and mouseup
  // (any parent state change does it), React replaces the <path> the press
  // landed on, and with the mousedown target detached the browser never fires
  // a click event at all — not on the chart, not on document. mouseup always
  // arrives, so the click is synthesised from it.
  const emitCursorClick = (distFt) => {
    const nearest = findNearest(seriesRef.current[0]?.points ?? [], distFt);
    if (nearest?.lat && nearest?.lon) {
      window.dispatchEvent(new CustomEvent('trackapp:chart-click', {
        detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime: nearest.csv_time ?? null },
      }));
    }
  };

  const finalizeZoom = () => {
    const pending = pendingDragRef.current;
    pendingDragRef.current = null;
    if (!isSelectingRef.current) {
      if (pending) emitCursorClick(pending.distFt);
      return;
    }
    isSelectingRef.current = false;
    setIsSelecting(false);

    const l = refAreaLeft.current;
    const r = refAreaRight.current;
    setSelectionViz(null);
    refAreaLeft.current  = null;
    refAreaRight.current = null;

    if (l == null || r == null) return;
    const [lo, hi] = l <= r ? [l, r] : [r, l];
    if (lo === hi) return;

    if (isAnnotateModeRef.current) {
      window.dispatchEvent(new CustomEvent('trackapp:annotation-captured', {
        detail: { channel: title, channelLabel: title, distFtStart: lo, distFtEnd: hi },
      }));
      setIsAnnotateMode(false);
      isAnnotateModeRef.current = false;
      window.dispatchEvent(new CustomEvent('trackapp:annotate-mode', { detail: { active: false } }));
    }
    publishZoom([lo, hi]);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: lo, distFtMax: hi },
    }));
  };
  finalizeZoomRef.current = finalizeZoom;

  const resetZoom = () => {
    publishZoom(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', { detail: null }));
  };

  const dispatchZoom = (domain) => {
    publishZoom(domain);
    window.dispatchEvent(new CustomEvent('trackapp:chart-zoom', {
      detail: { distFtMin: domain[0], distFtMax: domain[1] },
    }));
  };

  // Pan delta handler — shifts zoomDomain by deltaFt, clamped to natural extent
  const onPanFt = (deltaFt) => {
    const zoom = zoomDomainRef.current;
    if (!zoom) return;
    const nat    = naturalExtentRef.current;
    const natMin = nat ? nat[0] : zoom[0];
    const natMax = nat ? nat[1] : zoom[1];
    const size   = zoom[1] - zoom[0];
    let lo = zoom[0] + deltaFt;
    let hi = zoom[1] + deltaFt;
    if (lo < natMin) { hi = natMin + size; lo = natMin; }
    if (hi > natMax) { lo = natMax - size; hi = natMax; }
    dispatchZoom([lo, hi]);
  };
  const onPanFtRef = useRef(onPanFt);
  onPanFtRef.current = onPanFt;

  const onPinchZoom = (scaleFactor, centerFt) => {
    const nat    = naturalExtentRef.current;
    const zoom   = zoomDomainRef.current;
    const natMin = nat ? nat[0] : null;
    const natMax = nat ? nat[1] : null;
    let lo = zoom ? zoom[0] : natMin;
    let hi = zoom ? zoom[1] : natMax;
    if (lo == null || hi == null) return;
    const center = centerFt != null ? Math.max(lo, Math.min(hi, centerFt)) : (lo + hi) / 2;
    const newLo  = Math.max(natMin ?? lo, center - (center - lo) / scaleFactor);
    const newHi  = Math.min(natMax ?? hi, center + (hi - center) / scaleFactor);
    if (newHi - newLo < 200) return;
    dispatchZoom([newLo, newHi]);
  };
  const onPinchZoomRef = useRef(onPinchZoom);
  onPinchZoomRef.current = onPinchZoom;

  const chRightPadRef = useRef(RIGHT_MARGIN);
  usePanOnWheel({ chartWrapRef, zoomDomainRef, leftPad: Y_AXIS_W, rightPadRef: chRightPadRef, onPanFtRef });

  useTouchChartGestures({
    chartWrapRef,
    pixelToDistFtRef,
    onCrosshairMoveRef,
    onCrosshairClearRef,
    isSelectingRef,
    refAreaLeft,
    refAreaRight,
    setIsSelecting,
    setSelectionViz,
    finalizeZoomRef,
    isDirectHoverRef,
    onPanFtRef,
    onPinchZoomRef,
  });

  const handleMouseMove = (data, event) => {
    isDirectHoverRef.current = true;

    // Promote a pending press into a zoom drag once it has actually moved.
    if (pendingDragRef.current && !isSelectingRef.current) {
      const start = pendingDragRef.current;
      const moved = start.clientX != null && event?.clientX != null
        ? Math.abs(event.clientX - start.clientX)
        : DRAG_ARM_PX; // no clientX available — arm immediately (safe default)
      if (moved < DRAG_ARM_PX) return;
      isSelectingRef.current = true;
      setIsSelecting(true);
      refAreaLeft.current  = start.distFt;
      refAreaRight.current = start.distFt;
      setSelectionViz({ left: start.distFt, right: start.distFt });
    }

    if (isSelectingRef.current) {
      if (data?.activeLabel != null) {
        refAreaRight.current = Number(data.activeLabel);
        setSelectionViz({ left: refAreaLeft.current, right: refAreaRight.current });
      }
      return;
    }

    const coord  = data?.activeCoordinate;
    const distFt = data?.activeLabel != null ? Number(data.activeLabel) : null;
    if (!coord || distFt == null || !seriesRef.current.length) {
      setTooltipData(null);
      return;
    }

    const entries = seriesRef.current.map(s => {
      const pt = findNearest(s.points, distFt);
      if (!pt) return null;
      return { seriesKey: s.seriesKey, lapLabel: s.lapLabel, color: s.color, value: pt.value };
    }).filter(Boolean);

    let td = null;
    if (entries.length) {
      const ww = chartWrapRef.current?.clientWidth ?? 600;
      const frac = coord.x / ww;
      if (frac < 0.45) tooltipSideRef.current = 'right';
      else if (frac > 0.55) tooltipSideRef.current = 'left';
      td = { x: coord.x, distFt, entries, side: tooltipSideRef.current };
    }
    setTooltipData(td);

    const first = entries[0];
    if (first) {
      const nearest = findNearest(seriesRef.current[0]?.points ?? [], distFt);
      if (nearest?.lat && nearest?.lon) {
        window.dispatchEvent(new CustomEvent('trackapp:chart-hover', {
          detail: { lat: nearest.lat, lon: nearest.lon, distFt, videoTime: nearest.csv_time ?? null },
        }));
      }
    }
  };

  const handleMouseLeave = () => {
    isDirectHoverRef.current = false;
    pendingDragRef.current = null;
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      setIsSelecting(false);
      setSelectionViz(null);
      refAreaLeft.current  = null;
      refAreaRight.current = null;
    }
    setTooltipData(null);
    window.dispatchEvent(new CustomEvent('trackapp:chart-hover', { detail: null }));
  };

  const isEmpty = series.length === 0;

  const allVals = series.flatMap(s => s.points.map(p => p.value).filter(v => v != null));
  const computedDomain = yDomain || (allVals.length
    ? [Math.floor(arrayMin(allVals)), Math.ceil(arrayMax(allVals))]
    : [0, 100]);
  const [yMin, yMax] = computedDomain;
  // Digital/boolean channels (0/1) log discrete state changes, not a continuous
  // ramp — draw them as a square wave rather than linearly interpolating.
  const isBoolean = allVals.length > 0 && allVals.every(v => v === 0 || v === 1);

  // ── Asymmetric zero-split scaling (Power panel) ─────────────────────────────
  // When `zeroLine` is on and the data straddles zero, the y-axis is remapped to
  // a normalised [0,1] space with the zero crossing at `zeroFrac` (fraction from
  // the bottom). Each half is independently scaled to fill its band, so dragging
  // the axis handle trades vertical pixels between positive and braking power
  // without ever clipping either side's range.
  const naturalFrac = (yMin < 0 && yMax > 0) ? (-yMin) / (yMax - yMin) : null;
  const splittable  = zeroLine && naturalFrac != null;
  const zeroFrac    = splittable ? clamp(zeroFracRaw ?? naturalFrac, 0.08, 0.92) : null;

  const toPlot = (v) => {
    if (v == null) return null;
    if (v >= 0) return yMax > 0  ? zeroFrac + (v / yMax)   * (1 - zeroFrac) : zeroFrac;
    return        yMin < 0  ? zeroFrac + (v / -yMin) * zeroFrac       : zeroFrac;
  };
  const toReal = (p) => (p >= zeroFrac
    ? (p - zeroFrac) / (1 - zeroFrac) * yMax
    : (p - zeroFrac) / zeroFrac * -yMin);

  // Plotted copy of the series for rendering (Lines/dots); hover logic keeps
  // using the real-valued `series`, so tooltips still read true hp/kW.
  const renderSeries = useMemo(() => {
    if (!splittable) return series;
    return series.map(s => ({
      ...s,
      points: s.points.map(p => ({ ...p, value: toPlot(p.value) })),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, splittable, zeroFrac, yMin, yMax]);

  const plottedTicks = useMemo(
    () => (splittable ? niceTicks(yMin, yMax).map(toPlot) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [splittable, zeroFrac, yMin, yMax]
  );

  const valueToFrac = splittable
    ? toPlot
    : (v) => (yMax === yMin ? 0 : (v - yMin) / (yMax - yMin));

  const splitChartH  = (wrapHeight ?? 0) - CHART_TOP - CHART_BOTTOM;
  const zeroHandlePx = splittable ? CHART_TOP + (1 - zeroFrac) * splitChartH : null;

  const onZeroHandleMove = (e) => {
    if (!draggingZeroRef.current) return;
    const wrap = chartWrapRef.current;
    if (!wrap) return;
    const rect = wrap.getBoundingClientRect();
    const ch = rect.height - CHART_TOP - CHART_BOTTOM;
    if (ch <= 0) return;
    const frac = clamp(1 - (e.clientY - rect.top - CHART_TOP) / ch, 0.08, 0.92);
    zeroFracLiveRef.current = frac;
    setZeroFracRaw(frac);
  };
  const onZeroHandleDown = (e) => {
    e.stopPropagation();
    draggingZeroRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onZeroHandleUp = (e) => {
    draggingZeroRef.current = false;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    if (zeroFracLiveRef.current != null) {
      try { localStorage.setItem(zeroFracKey, String(zeroFracLiveRef.current)); } catch { /* ignore */ }
    }
  };
  const onZeroHandleReset = (e) => {
    e.stopPropagation();
    zeroFracLiveRef.current = null;
    setZeroFracRaw(null);
    try { localStorage.removeItem(zeroFracKey); } catch { /* ignore */ }
  };

  const handleExportPng = () => {
    const svg = chartWrapRef.current?.querySelector('svg.recharts-surface')
      || chartWrapRef.current?.querySelector('svg');
    if (!svg || !series.length) return;
    exportTracePanelPng({
      svgs: [svg],
      themeFrom: chartWrapRef.current,
      series,
      fallbackTitle: title,
      subtitle: `${signalName}${yLabel ? ` (${yLabel})` : ''} vs Distance`,
      slugBits: [signalName || 'channel', exportSlug],
      plotTop: CHART_TOP,
      plotBottom: CHART_BOTTOM,
      trackName,
      sessionDate,
      sessionVehicle,
      driverName1,
      exportCompareDrivers,
    }).catch(error => console.error('Chart export failed:', error));
  };

  return (
    <div className={styles.panel} style={height != null ? { height, minHeight: height } : { height: '100%' }}>
      <PanelHeader className={styles.header} title={<span className={styles.title}>{title}</span>}>
        <div className={styles.opts}>
          {headerExtra}
          {onAdditionalChannelToggle && (
            <ChannelPicker
              availableChannels={availableChannels}
              selectedChannels={selectedAdditionalChannels}
              onToggle={onAdditionalChannelToggle}
            />
          )}
          <button
            type="button"
            className={styles.optBtn}
            onClick={handleExportPng}
            title="Download this chart as a PNG image with a driver / lap-time legend"
          >PNG ⤓</button>
          <button
            className={`${styles.optBtn} ${showMinMax ? styles.optActive : ''}`}
            onClick={() => setShowMinMax(v => !v)}
          >Min/Max</button>
          <HelpButton panel={helpPanel} page="sessions" className={styles.optBtn} />
        </div>
      </PanelHeader>

      <div
        className={styles.chartWrap}
        ref={chartWrapRef}
        onDoubleClick={resetZoom}
        style={{
          position: 'relative',
          cursor: isSelecting
            ? (isAnnotateMode ? 'crosshair' : 'ew-resize')
            : (isAnnotateMode ? 'crosshair' : 'col-resize'),
          userSelect: 'none',
          outline: isAnnotateMode ? '2px solid #4f9cf5' : 'none',
          outlineOffset: '-2px',
        }}
      >
        {/* Yellow hover line */}
        <div
          ref={hoverLineRef}
          style={{
            display: 'none', position: 'absolute',
            top: 8, bottom: 4, width: 1,
            background: '#f5c842', opacity: 0.85,
            pointerEvents: 'none', zIndex: 10,
          }}
        />

        <HoverDots
          data={tooltipData}
          valueToFrac={valueToFrac}
          wrapHeight={wrapHeight}
        />

        {/* Draggable zero-line handle on the Y-axis — moves the positive/braking split */}
        {splittable && wrapHeight != null && splitChartH > 0 && (
          <div
            onPointerDown={onZeroHandleDown}
            onPointerMove={onZeroHandleMove}
            onPointerUp={onZeroHandleUp}
            onMouseDown={e => e.stopPropagation()}
            onDoubleClick={onZeroHandleReset}
            title="Drag to move the zero line — trades panel height between positive and braking power (double-click to auto-fit)"
            style={{
              position: 'absolute',
              left: 0,
              top: zeroHandlePx - 9,
              width: Y_AXIS_W + 13,
              height: 18,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              cursor: 'ns-resize',
              zIndex: 16,
              touchAction: 'none',
            }}
          >
            <div style={{
              width: 16, height: 14, borderRadius: 3,
              background: chartColors.axis,
              border: '1px solid rgba(255,255,255,0.25)',
              boxShadow: '0 0 3px rgba(0,0,0,0.55)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#0b0e1a', fontSize: 10, lineHeight: 1, fontWeight: 700,
            }}>⇕</div>
          </div>
        )}

        <HoverTooltip
          data={tooltipData}
          yLabel={yLabel}
          wrapWidth={chartWrapRef.current?.clientWidth}
          anchoredTooltip={anchoredTooltip}
          unitPrefs={unitPrefs}
        />

        {isEmpty ? (
          <div className={styles.empty}>No {title.toLowerCase()} data</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              syncId="track"
              syncMethod="value"
              accessibilityLayer={false}
              margin={{ top: CHART_TOP, right: RIGHT_MARGIN, bottom: 4, left: 0 }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={finalizeZoom}
              onMouseLeave={handleMouseLeave}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />

              <XAxis
                dataKey="dist_ft"
                type="number"
                domain={zoomDomain || naturalExtent || ['auto', 'auto']}
                tickFormatter={v => `${(convertDistanceFt(v, unitPrefs) / 1000).toFixed(1)}k`}
                stroke={chartColors.axis}
                tick={{ fill: chartColors.tick, fontSize: 10 }}
                height={18}
                allowDuplicatedCategory={false}
                label={{ value: `${getDistanceUnit(unitPrefs)} from lap start`, position: 'insideBottomRight', offset: -4, fill: chartColors.label, fontSize: 9 }}
              />

              <YAxis
                yAxisId="val"
                domain={splittable ? [0, 1] : computedDomain}
                ticks={splittable ? plottedTicks : undefined}
                stroke={chartColors.axis}
                tick={{ fill: chartColors.tick, fontSize: 10 }}
                tickFormatter={splittable ? (p => fmtAxis(toReal(p))) : yTickFormatter}
                label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: 16, fill: chartColors.label, fontSize: 10 }}
                width={Y_AXIS_W}
              />

              {renderSeries.map(({ seriesKey, lapLabel, color, points }) => (
                <Line
                  key={seriesKey}
                  yAxisId="val"
                  data={points}
                  dataKey="value"
                  name={lapLabel}
                  type={isBoolean ? 'stepAfter' : undefined}
                  stroke={color}
                  strokeWidth={1.5}
                  dot={false}
                  isAnimationActive={false}
                  activeDot={false}
                />
              ))}

              {/* Zero reference line — distinguishes positive power from braking.
                  When split-scaling is active it sits at the draggable `zeroFrac`. */}
              {splittable && (
                <ReferenceLine
                  yAxisId="val"
                  y={zeroFrac}
                  stroke={chartColors.axis}
                  strokeWidth={1.5}
                  strokeOpacity={0.85}
                />
              )}

              {/* Min/Max dots */}
              {showMinMax && series.flatMap(({ seriesKey, color, maxPt, minPt }) => [
                maxPt && (
                  <ReferenceDot
                    key={`max-${seriesKey}`}
                    yAxisId="val"
                    x={maxPt.dist_ft}
                    y={splittable ? toPlot(maxPt.value) : maxPt.value}
                    r={5}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={1.5}
                    label={{
                      value: fmtVal(maxPt.value),
                      position: 'bottom',
                      fill: color, fontSize: 10, fontWeight: 700,
                    }}
                  />
                ),
                minPt && (
                  <ReferenceDot
                    key={`min-${seriesKey}`}
                    yAxisId="val"
                    x={minPt.dist_ft}
                    y={splittable ? toPlot(minPt.value) : minPt.value}
                    r={5}
                    fill={color}
                    stroke="#fff"
                    strokeWidth={1.5}
                    label={{
                      value: fmtVal(minPt.value),
                      position: 'top',
                      fill: color, fontSize: 10, fontWeight: 700,
                    }}
                  />
                ),
              ].filter(Boolean))}

              {/* Corner highlight from the Corner Metrics panel */}
              {cornerHl?.distFtMin != null && (
                <ReferenceArea
                  yAxisId="val"
                  x1={cornerHl.distFtMin}
                  x2={cornerHl.distFtMax}
                  fill="#f5c842"
                  fillOpacity={0.07}
                  stroke="#f5c842"
                  strokeOpacity={0.35}
                  strokeDasharray="4 3"
                />
              )}

              {/* Drag selection highlight — yellow for zoom, blue for annotate */}
              {selectionViz && selectionViz.left !== selectionViz.right && (
                <ReferenceArea
                  yAxisId="val"
                  x1={Math.min(selectionViz.left, selectionViz.right)}
                  x2={Math.max(selectionViz.left, selectionViz.right)}
                  fill={isAnnotateMode ? '#4f9cf5' : '#f5c842'}
                  fillOpacity={0.15}
                  stroke={isAnnotateMode ? '#4f9cf5' : '#f5c842'}
                  strokeOpacity={0.6}
                />
              )}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

// Generic channel windows opt into the multi-signal renderer even before an
// extra is selected so their header can expose the panel-local Channels picker.
// Power/formula callers keep the mature single-signal path above unchanged.
export default function ChannelTrace(props) {
  if (props.availableChannels || props.additionalSignals) {
    return <MultiChannelTrace {...props} />;
  }
  return <SingleChannelTrace {...props} />;
}
